
import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment-timezone';
import { KycRequests } from '@/api';
import { KycOrgConfig, GeneralDoc, OptionalDocs } from '@/api/auth';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import PhotoType from '@/resources/enum/PhotoType';
import ApiCancelError from '@/resources/error/ApiCancelError';
import ApiModalError from '@/resources/error/ApiModalError';
import PageHeader from '@/components/PageHeader.vue';
import FooterButton from '@/components/FooterButton.vue';
import TopContentTitle from '@/components/TopContentTitle.vue';
import TopContentText from '@/components/TopContentText.vue';
import TopContentSelect from '@/components/TopContentSelect.vue';
import TopContentSelectGpDoc from '@/components/TopContentSelectGpDoc.vue';
import TopContentSelectOpDoc from '@/components/TopContentSelectOpDoc.vue';
import TopContentInformation from '@/components/TopContentInformation.vue';
import TopContentCautionList from '@/components/TopContentCautionList.vue';
import TopContentCautionContactUs from '@/components/TopContentCautionContactUs.vue';
import TopContentCheck from '@/components/TopContentCheck.vue';
import ScrollToBottomButton from '@/components/ScrollToBottomButton.vue';
import { PersonalInformation as PersonalInformationApi } from '@/api';
import { TranslateResult } from 'vue-i18n';

@Component({
  components: {
    TopContentCautionContactUs,
    PageHeader,
    FooterButton,
    TopContentTitle,
    TopContentText,
    TopContentSelect,
    TopContentSelectGpDoc,
    TopContentSelectOpDoc,
    TopContentInformation,
    TopContentCautionList,
    TopContentCheck,
    ScrollToBottomButton
  }
})
export default class PersonalInformation extends Vue {
  private isFirstLoading: boolean = false;
  private isLastLoading: boolean = false;
  private isChecked: boolean = false;
  private idDocTypeId: IdDocType = IdDocType.Unknown;
  private mnDocTypeId: MnDocType = MnDocType.Unknown;
  private gpDocTypeId: string = 'unknown';
  private opDocTypeId: string[] = [];
  private firstName: string = '';
  private middleName: string = '';
  private lastName: string = '';
  private birthDay: string = '';
  private zipCode: string = '';
  private maskedAddress1: string = '';
  private maskedAddress2: string = '';
  private maskedAddress3: string = '';
  private maskedAddress4: string = '';

  // 書類選択画面（この画面）をスキップする
  private get shouldSkipDocumentSelection(): boolean {
    const pageTransitionMode: number = this.$store.getters['auth/pageTransitionMode'];
    return pageTransitionMode === 1;
  }

  // ボタン有効確認
  private get enabled(): boolean {
    return this.isChecked;
  }

  private get isIddocList(): boolean {
    if (this.selectGpDocList.length > 0 && this.$store.getters['auth/isOnlyGpGeneral']) return false;
    return true;
  }

  private get isMndocList(): boolean {
    if (this.selectGpDocList.length > 0 && this.$store.getters['auth/isOnlyGpGeneral']) return false;
    return this.selectMnDocList.length > 0;
  }

  private get isGpdocList(): boolean {
    return this.selectGpDocList.length > 0;
  }

  private get isOpdocList(): boolean {
    if (this.selectGpDocList.length > 0 && this.$store.getters['auth/isOnlyGpGeneral']) return false;
    return this.selectOpDocList.length > 0;
  }

  private get fullName(): string[] {
    let fullname: string = '';
    if (this.$i18n.locale !== 'ja') {
      fullname = `${this.lastName} ${this.firstName} ${this.middleName}`;
    } else {
      fullname = `${this.lastName} ${this.middleName} ${this.firstName}`;
    }
    return [fullname.trim().replace(/ +/g, '\u3000')];
  }

  private get formattedBirthDay(): string[] {
    return [moment(this.birthDay).tz('Asia/Tokyo').format(this.$tc('202309_1465'))];
  }

  private get fullAddress(): string[] {
    return [this.maskedAddress1 + this.maskedAddress2 + this.maskedAddress3 + this.maskedAddress4];
  }

  private get orgName(): string {
    return this.$store.getters['auth/currentLanguageOrgName'];
  }

  private get checkKYCRequestInformation(): boolean {
    const kycOrgConfig: KycOrgConfig = this.$store.getters['auth/kycOrgConfig'];
    return kycOrgConfig.checkKYCRequestInformation;
  }

  private get headerBar(): string {
    if (this.checkKYCRequestInformation) {
      return this.$t('202309_114') as string;
    } else if (this.selectGpDocList.length > 0) {
      return this.$t('202309_208') as string;
    } else if (this.$store.getters['auth/isCollectMnDocOnly']) {
      return this.$t('202309_194') as string;
    } else {
      return this.$t('202309_155') as string;
    }
  }

  private get headerText(): string {
    if (this.selectGpDocList.length > 0) {
      return this.$t('202309_209') as string;
    } else if (this.$store.getters['auth/isCollectMnDocOnly']) {
      return this.$t('202309_195') as string;
    } else {
      if (this.checkKYCRequestInformation) {
        return this.$t('202309_115') as string;
      } else {
        return this.$t('202309_156') as string;
      }
    }
  }

  private get informationList(): {
    label: string;
    values: string[];
  }[] {
    const checkName: string = this.$t('202309_144', {}) as string;
    const checkBirth: string = this.$t('202309_145', {}) as string;
    const checkAddress: string = this.$t('202309_146', {}) as string;
    return [
      { label: checkName, values: this.fullName },
      { label: checkBirth, values: this.formattedBirthDay },
      { label: checkAddress, values: this.fullAddress }
    ];
  }

  private get cautionList(): string[] {
    if (this.isMndocList) {
      return [this.$t('202309_203'), this.$t('202309_205'), this.$t('202309_204')] as string[];
    }

    if (this.isGpdocList) {
      return [this.$t('202309_216'), this.$t('202309_218'), this.$t('202309_217')] as string[];
    }

    if (this.checkKYCRequestInformation) {
      return [this.$t('202309_148'), this.$t('202309_149'), this.$t('202309_150'), this.$t('202309_151')] as string[];
    }

    return [this.$t('202309_181'), this.$t('202309_182'), this.$t('202309_183')] as string[];
  }

  private get checkText(): string {
    if (this.checkKYCRequestInformation) {
      return this.$t('202309_152') as string;
    }

    return this.$t('202309_184') as string;
  }

  private get selectIdDocList(): {
    key: IdDocType;
    text: string;
  }[] {
    let idDocTypes: IdDocType[] = this.$store.getters['auth/idDocTypes'];

    const selectorTexts: { [key in IdDocType]: TranslateResult } = {
      [IdDocType.DriversLicenseHo]: this.$t('202309_158'),
      [IdDocType.DriversLicenseTo]: this.$t('202309_158'),
      [IdDocType.DriversLicenseTi]: this.$t('202309_158'),
      [IdDocType.MyNumberCardHo]: this.$t('202309_160'),
      [IdDocType.MyNumberCardTo]: this.$t('202309_160'),
      [IdDocType.MyNumberCardTi]: this.$t('202309_160'),
      [IdDocType.ResidenceCardHo]: this.$t('202309_159'),
      [IdDocType.ResidenceCardTo]: this.$t('202309_159'),
      [IdDocType.ResidenceCardTi]: this.$t('202309_159'),
      [IdDocType.SpecialPermanentResidentCertificateHo]: this.$t('202309_179'),
      [IdDocType.SpecialPermanentResidentCertificateTo]: this.$t('202309_179'),
      [IdDocType.SpecialPermanentResidentCertificateTi]: this.$t('202309_179'),
      [IdDocType.PassportHo]: this.$t('202309_120'),
      [IdDocType.PassportTo]: this.$t('202309_120'),
      [IdDocType.PassportTi]: this.$t('202309_120'),
      [IdDocType.DrivingHistoryCertificateHo]: this.$t('202309_162'),
      [IdDocType.DrivingHistoryCertificateTo]: this.$t('202309_162'),
      [IdDocType.DrivingHistoryCertificateTi]: this.$t('202309_162'),
      [IdDocType.HealthInsuranceCardTo]: this.$t('202309_164'),
      [IdDocType.HealthInsuranceCardTi]: this.$t('202309_164'),
      [IdDocType.PhysicalDisabilityCertificateBookTo]: this.$t('202309_165'),
      [IdDocType.PhysicalDisabilityCertificateBookTi]: this.$t('202309_165'),
      [IdDocType.PhysicalDisabilityCertificateCardTo]: this.$t('202309_172'),
      [IdDocType.PhysicalDisabilityCertificateCardTi]: this.$t('202309_172'),
      [IdDocType.SpecialEducationBookTo]: this.$t('202309_166'),
      [IdDocType.SpecialEducationBookTi]: this.$t('202309_166'),
      [IdDocType.SpecialEducationCardTo]: this.$t('202309_171'),
      [IdDocType.SpecialEducationCardTi]: this.$t('202309_171'),
      [IdDocType.StudentNotebookTo]: this.$t('202309_176'),
      [IdDocType.StudentNotebookTi]: this.$t('202309_176'),
      [IdDocType.MentalDisabilityCertificateBookTo]: this.$t('202309_167'),
      [IdDocType.MentalDisabilityCertificateBookTi]: this.$t('202309_167'),
      [IdDocType.MentalDisabilityCertificateCardTo]: this.$t('202309_170'),
      [IdDocType.MentalDisabilityCertificateCardTi]: this.$t('202309_170'),
      [IdDocType.PensionBookTo]: this.$t('202309_168'),
      [IdDocType.PensionBookTi]: this.$t('202309_168'),
      [IdDocType.BasicPensionNumberNotificationTo]: this.$t('202309_169'),
      [IdDocType.BasicPensionNumberNotificationTi]: this.$t('202309_169'),
      [IdDocType.PassportAnyIssuanceDateTo]: this.$t('202309_173'),
      [IdDocType.PassportAnyIssuanceDateTi]: this.$t('202309_173'),
      [IdDocType.BasicResidentRegistrationCardTo]: this.$t('202309_178'),
      [IdDocType.BasicResidentRegistrationCardTi]: this.$t('202309_178'),
      [IdDocType.StudentIDCardTo]: this.$t('202309_175'),
      [IdDocType.StudentIDCardTi]: this.$t('202309_175'),
      [IdDocType.MnGeneral]: this.$t('202309_197'),
      [IdDocType.GpGeneral]: this.$t('202309_20100'),
      [IdDocType.MyNumberCardAndBack]: this.$t('202309_160'),
      [IdDocType.DriversLicenseGlobalNoFace]: this.$t('202309_158'),
      [IdDocType.NationalIdentityCardGlobalNoFace]: this.$t('202309_119'),
      [IdDocType.ResidenceCardGlobalNoFace]: this.$t('202309_159'),
      [IdDocType.PassportGlobalNoFace]: this.$t('202407_002'),
      [IdDocType.PhilIDGlobalNoFace]: this.$t('202309_139'),
      [IdDocType.TINIDGlobalNoFace]: this.$t('202309_140'),
      [IdDocType.SSSIDGlobalNoFace]: this.$t('202309_141'),
      [IdDocType.UMIDGlobalNoFace]: this.$t('202309_1415'),
      [IdDocType.UMID2GlobalNoFace]: this.$t('202309_142'),
      [IdDocType.NRICGlobalNoFace]: this.$t('20231005_001'),
      [IdDocType.WorkPermitGlobalNoFace]: this.$t('20231005_002'),
      [IdDocType.KTPGlobalNoFace]: this.$t('20231005_003'),
      [IdDocType.KIAOver5GlobalNoFace]: this.$t('20231005_004'),
      [IdDocType.KIAUnder5GlobalNoFace]: this.$t('20231005_005'),
      [IdDocType.KITAPGlobalNoFace]: this.$t('20231005_006'),
      [IdDocType.CCCDGlobalNoFace]: this.$t('20231005_007'),
      [IdDocType.CMNDGlobalNoFace]: this.$t('20231005_008'),
      [IdDocType.MyPRGlobalNoFace]: this.$t('20231005_009'),
      [IdDocType.MyKADGlobalNoFace]: this.$t('20231005_010'),
      [IdDocType.AadhaarGlobalNoFace]: this.$t('20231005_011'),
      [IdDocType.DriversLicenseGlobalFace]: this.$t('202309_158'),
      [IdDocType.NationalIdentityCardGlobalFace]: this.$t('202309_119'),
      [IdDocType.ResidenceCardGlobalFace]: this.$t('202309_159'),
      [IdDocType.PassportGlobalFace]: this.$t('202407_002'),
      [IdDocType.PhilIDGlobalFace]: this.$t('202309_139'),
      [IdDocType.TINIDGlobalFace]: this.$t('202309_140'),
      [IdDocType.SSSIDGlobalFace]: this.$t('202309_141'),
      [IdDocType.UMIDGlobalFace]: this.$t('202309_1415'),
      [IdDocType.UMID2GlobalFace]: this.$t('202309_142'),
      [IdDocType.NRICGlobalFace]: this.$t('20231005_001'),
      [IdDocType.WorkPermitGlobalFace]: this.$t('20231005_002'),
      [IdDocType.KTPGlobalFace]: this.$t('20231005_003'),
      [IdDocType.KIAOver5GlobalFace]: this.$t('20231005_004'),
      [IdDocType.KITAPGlobalFace]: this.$t('20231005_006'),
      [IdDocType.CCCDGlobalFace]: this.$t('20231005_007'),
      [IdDocType.CMNDGlobalFace]: this.$t('20231005_008'),
      [IdDocType.MyPRGlobalFace]: this.$t('20231005_009'),
      [IdDocType.MyKADGlobalFace]: this.$t('20231005_010'),
      [IdDocType.AadhaarGlobalFace]: this.$t('20231005_011'),
      [IdDocType.Unknown]: this.$t('202309_1425'),
      [IdDocType.DriversLicenseFrontFace]: this.$t('202309_158'),
      [IdDocType.MyNumberCardFrontFace]: this.$t('202309_160'),
      [IdDocType.ResidenceCardFrontFace]: this.$t('202309_159'),
      [IdDocType.SpecialPermanentResidentCertificateFrontFace]: this.$t('202309_179'),
      [IdDocType.PassportFrontFace]: this.$t('202309_120'),
      [IdDocType.DrivingHistoryCertificateFrontFace]: this.$t('202309_122'),
      [IdDocType.PassportAnyIssuanceDateFrontFace]: this.$t('202309_173'),
      [IdDocType.ForeignPassportFrontFace]: this.$t('202309_133'),
      [IdDocType.StudentIDCardFrontFace]: this.$t('202309_175'),
      [IdDocType.BasicResidentRegistrationCardFrontFace]: this.$t('202309_178'),
      [IdDocType.StudentNotebookFrontFace]: this.$t('202309_176'),
      [IdDocType.DriversLicenseFront]: this.$t('202309_158'),
      [IdDocType.MyNumberCardFront]: this.$t('202309_160'),
      [IdDocType.ResidenceCardFront]: this.$t('202309_159'),
      [IdDocType.SpecialPermanentResidentCertificateFront]: this.$t('202309_179'),
      [IdDocType.PassportFront]: this.$t('202309_120'),
      [IdDocType.DrivingHistoryCertificateFront]: this.$t('202309_122'),
      [IdDocType.PassportAnyIssuanceDateFront]: this.$t('202309_173'),
      [IdDocType.ForeignPassportFront]: this.$t('202309_133'),
      [IdDocType.HealthInsuranceCardFront]: this.$t('202309_164'),
      [IdDocType.PhysicalDisabilityCertificateCardFront]: this.$t('202309_172'),
      [IdDocType.MentalDisabilityCertificateCardFront]: this.$t('202309_170'),
      [IdDocType.SpecialEducationCardFront]: this.$t('202309_171'),
      [IdDocType.StudentIDCardFront]: this.$t('202309_175'),
      [IdDocType.BasicResidentRegistrationCardFront]: this.$t('202309_178'),
      [IdDocType.EmployeeIDCardFront]: this.$t('202309_136'),
      [IdDocType.PhysicalDisabilityCertificateBookFront]: this.$t('202309_165'),
      [IdDocType.MentalDisabilityCertificateBookFront]: this.$t('202309_167'),
      [IdDocType.SpecialEducationBookFront]: this.$t('202309_166'),
      [IdDocType.PensionBookFront]: this.$t('202309_168'),
      [IdDocType.BasicPensionNumberNotificationFront]: this.$t('202309_169'),
      [IdDocType.StudentNotebookFront]: this.$t('202309_176'),
      [IdDocType.MotherAndBabyNotebookFront]: this.$t('202404_005'),
      [IdDocType.CertificateOfResidenceFront]: this.$t('202404_006'),
      [IdDocType.CertificateOfFamilyRegisterFront]: this.$t('202404_007'),
      [IdDocType.CertificateOfIndividualRecordsFront]: this.$t('202404_008'),
      [IdDocType.DriversLicenseFrontBackFace]: this.$t('202309_158'),
      [IdDocType.MyNumberCardFrontBackFace]: this.$t('202309_160'),
      [IdDocType.ResidenceCardFrontBackFace]: this.$t('202309_159'),
      [IdDocType.SpecialPermanentResidentCertificateFrontBackFace]: this.$t('202309_179'),
      [IdDocType.PassportFrontBackFace]: this.$t('202309_120'),
      [IdDocType.DrivingHistoryCertificateFrontBackFace]: this.$t('202309_122'),
      [IdDocType.PassportAnyIssuanceDateFrontBackFace]: this.$t('202309_173'),
      [IdDocType.ForeignPassportFrontBackFace]: this.$t('202309_133'),
      [IdDocType.StudentIDCardFrontBackFace]: this.$t('202309_175'),
      [IdDocType.BasicResidentRegistrationCardFrontBackFace]: this.$t('202309_178'),
      [IdDocType.StudentNotebookFrontBackFace]: this.$t('202309_176'),
      [IdDocType.DriversLicenseFrontBack]: this.$t('202309_158'),
      [IdDocType.MyNumberCardFrontBack]: this.$t('202309_160'),
      [IdDocType.ResidenceCardFrontBack]: this.$t('202309_159'),
      [IdDocType.SpecialPermanentResidentCertificateFrontBack]: this.$t('202309_179'),
      [IdDocType.PassportFrontBack]: this.$t('202309_120'),
      [IdDocType.DrivingHistoryCertificateFrontBack]: this.$t('202309_122'),
      [IdDocType.PassportAnyIssuanceDateFrontBack]: this.$t('202309_173'),
      [IdDocType.ForeignPassportFrontBack]: this.$t('202309_133'),
      [IdDocType.HealthInsuranceCardFrontBack]: this.$t('202309_164'),
      [IdDocType.PhysicalDisabilityCertificateCardFrontBack]: this.$t('202309_172'),
      [IdDocType.MentalDisabilityCertificateCardFrontBack]: this.$t('202309_170'),
      [IdDocType.SpecialEducationCardFrontBack]: this.$t('202309_171'),
      [IdDocType.StudentIDCardFrontBack]: this.$t('202309_175'),
      [IdDocType.BasicResidentRegistrationCardFrontBack]: this.$t('202309_178'),
      [IdDocType.EmployeeIDCardFrontBack]: this.$t('202309_136'),
      [IdDocType.PhysicalDisabilityCertificateBookFrontBack]: this.$t('202309_165'),
      [IdDocType.MentalDisabilityCertificateBookFrontBack]: this.$t('202309_167'),
      [IdDocType.SpecialEducationBookFrontBack]: this.$t('202309_166'),
      [IdDocType.PensionBookFrontBack]: this.$t('202309_168'),
      [IdDocType.BasicPensionNumberNotificationFrontBack]: this.$t('202309_169'),
      [IdDocType.StudentNotebookFrontBack]: this.$t('202309_176'),
      [IdDocType.MotherAndBabyNotebookFrontBack]: this.$t('202404_005'),
      [IdDocType.CertificateOfResidenceFrontBack]: this.$t('202404_006'),
      [IdDocType.CertificateOfFamilyRegisterFrontBack]: this.$t('202404_007'),
      [IdDocType.CertificateOfIndividualRecordsFrontBack]: this.$t('202404_008')
    };

    return idDocTypes.map((idDocType) => ({
      key: idDocType,
      text: selectorTexts[idDocType] as string
    }));
  }

  private get selectMnDocList(): {
    key: MnDocType;
    text: string;
  }[] {
    // 個人番号確認書類はPCに対応していないので、PCだと選択できないようにする
    if (this.$store.getters['camera/isPC']) return [];
    if (!this.$store.getters['auth/isCollectMnDoc']) return [];

    const selectorTexts: { [key in MnDocType]: TranslateResult } = {
      [MnDocType.MyNumberCard]: this.$t('202309_199'),
      [MnDocType.NotificationCard]: this.$t('202309_200'),
      [MnDocType.ResidenceCertificate]: this.$t('202309_201'),
      [MnDocType.Unknown]: this.$t('202309_2005')
    };

    const mnDocTypes: MnDocType[] = this.$store.getters['auth/myNumberDocTypes'];
    return mnDocTypes.map((mnDocType) => ({
      key: mnDocType,
      text: selectorTexts[mnDocType] as string
    }));
  }

  private get selectGpDocList(): {
    key: string;
    page: number;
    text1: string;
    text2: string;
    text3: string;
    srcset: string[] | null;
  }[] {
    if (!this.$store.getters['auth/isCollectGpDoc']) return [];

    const locale: string = this.$store.getters['i18n/locale'];
    let generalDocs: GeneralDoc[] = this.$store.getters['auth/generalDocs'];
    generalDocs = Array.from(generalDocs).sort((a, b): number => a.displayPriority - b.displayPriority);
    return generalDocs.map((generalDoc) => {
      const key = this.$t('202309_212', {
        _最大枚数: generalDoc.maxPages
      });
      return {
        key: generalDoc.generalDocType,
        page: generalDoc.maxPages,
        text1: generalDoc.generalDocName[locale],
        text2: key as string,
        text3: generalDoc.description[locale],
        srcset: null
      };
    });
  }

  private get selectOpDocList(): {
    key: string;
    seqNo: number;
    page: number;
    text1: string;
    text2: string;
    text3: string;
  }[][] {
    const locale: string = this.$store.getters['i18n/locale'];
    let optionalDocs: OptionalDocs[] = this.$store.getters['auth/optionalDocs'];
    optionalDocs = Array.from(optionalDocs).sort((a, b): number => a.seqNo - b.seqNo);
    return optionalDocs.map((valueOuter) => {
      const sorted = Array.from(valueOuter.selection).sort((a, b): number => a.displayPriority - b.displayPriority);
      return sorted.map((valueInner) => ({
        key: valueInner.optionalDocType,
        seqNo: valueOuter.seqNo,
        page: valueInner.maxPages,
        text1: valueInner.optionalDocName[locale],
        text2: '',
        text3: valueInner.description[locale]
      }));
    });
  }

  public get scrollTarget() {
    return document.scrollingElement;
  }

  public get scrollEventTarget() {
    return window;
  }

  // 初期化処理
  private async created() {
    // 本人確認書類のデフォルトの選択
    const currentIdDocType: IdDocType = this.$store.getters['camera/idDocType'];
    if (currentIdDocType !== IdDocType.Unknown) {
      this.idDocTypeId = currentIdDocType;
    } else if (this.selectIdDocList.length > 0) {
      this.idDocTypeId = this.selectIdDocList[0].key;
    }

    // 個人番号確認書類のデフォルトの選択
    const currentMnDocType: MnDocType = this.$store.getters['camera/mnDocType'];
    if (currentMnDocType !== MnDocType.Unknown) {
      this.mnDocTypeId = currentMnDocType;
    } else if (this.selectMnDocList.length > 0) {
      this.mnDocTypeId = this.selectMnDocList[0].key;
    }

    // 汎用書類のデフォルトの選択
    const currentGpDocType: string = this.$store.getters['camera/gpDocType'];
    if (currentGpDocType !== 'unknown') {
      this.gpDocTypeId = currentGpDocType;
    } else if (this.selectGpDocList.length > 0) {
      this.gpDocTypeId = this.selectGpDocList[0].key;
    }

    // 補助書類のデフォルトの選択
    const currentOpDocType: string[] = this.$store.getters['camera/opDocType'];
    if (currentOpDocType.length > 0) {
      this.opDocTypeId = currentOpDocType;
    } else if (this.selectOpDocList.length > 0) {
      this.opDocTypeId = this.selectOpDocList.map((value) => value[0].key);
    }

    if (this.checkKYCRequestInformation) {
      this.isFirstLoading = true;
      const kycRequestId: number = this.$store.getters['auth/kycRequestID'];

      try {
        const response = await PersonalInformationApi.fetch(kycRequestId);
        this.firstName = response.data.firstName;
        this.middleName = response.data.middleName;
        this.lastName = response.data.lastName;
        this.birthDay = response.data.birthday;
        this.zipCode = response.data.zipCode;
        this.maskedAddress1 = response.data.maskedAddress1;
        this.maskedAddress2 = response.data.maskedAddress2;
        this.maskedAddress3 = response.data.maskedAddress3;
        this.maskedAddress4 = response.data.maskedAddress4;
      } catch (error) {
        if (error instanceof ApiCancelError) {
          // キャンセルは握りつぶす
        } else if (error instanceof ApiModalError) {
          // clientRetryableで処理したエラーは握りつぶす
        } else throw error;
      } finally {
        this.isFirstLoading = false;
      }
    } else {
      this.isFirstLoading = true;
      await new Promise((resolve) => window.setTimeout(resolve, 100));
      this.isFirstLoading = false;
    }

    if (this.shouldSkipDocumentSelection) {
      this.next();
    }
  }

  // 次のページへ行く
  private async next(): Promise<void> {
    const kycOrgConfig: KycOrgConfig = this.$store.getters['auth/kycOrgConfig'];

    try {
      this.isLastLoading = true;
      const kycRequestId: number = this.$store.getters['auth/kycRequestID'];
      const isFetchMnDocTypes: boolean = this.$store.getters['auth/isCollectMnDoc'];
      const isFetchGpDocTypes: boolean = this.$store.getters['auth/isCollectGpDoc'];
      const isDetectableIdDoc: boolean = kycOrgConfig.autoShootIDDocTypes.indexOf(this.idDocTypeId) >= 0;
      const responseIdDocTypes = await KycRequests.fetchIdDocTypes(kycRequestId, { idDocType: this.idDocTypeId });
      const disabledAutoShootPhotoTypes = responseIdDocTypes?.data?.disabledAutoShootPhotoTypes ?? [];
      const isManualFront: boolean = disabledAutoShootPhotoTypes.includes(PhotoType.Front);
      const isManualDiagonal: boolean = disabledAutoShootPhotoTypes.includes(PhotoType.Diagonal);
      const isManualBack: boolean = disabledAutoShootPhotoTypes.includes(PhotoType.Back);
      if (isFetchMnDocTypes) await KycRequests.fetchMnDocTypes(kycRequestId, { myNumberDocType: this.mnDocTypeId });
      if (isFetchGpDocTypes) await KycRequests.fetchGpDocTypes(kycRequestId, { generalDocType: this.gpDocTypeId });
      const gpDocPage: number = this.selectGpDocList.find((data) => data.key === this.gpDocTypeId)?.page || 0;
      this.sendIdDocTypeIDForGA();
      this.$store.commit('camera/setIdDocType', this.idDocTypeId);
      this.$store.commit('camera/setMnDocType', this.mnDocTypeId);
      this.$store.commit('camera/setGpDocType', this.gpDocTypeId);
      this.$store.commit('camera/setGpDocPageLast', gpDocPage);
      this.$store.commit('camera/setOpDocType', this.opDocTypeId);
      this.$store.commit('camera/isDetectableIdDocFront', isDetectableIdDoc && !isManualFront);
      this.$store.commit('camera/isDetectableIdDocDiagonal', isDetectableIdDoc && !isManualDiagonal);
      this.$store.commit('camera/isDetectableIdDocBack', isDetectableIdDoc && !isManualBack);
      this.$store.commit('camera/isDetectStop', false);
      this.$store.commit('camera/resetRetryCount');

      // 書類選択画面スキップ時はこの画面の履歴を残さないためにreplaceを使用する
      this.shouldSkipDocumentSelection
        ? this.$router.replace(this.$store.getters['camera/locationFirst'])
        : this.$router.push(this.$store.getters['camera/locationFirst']);
    } catch (error) {
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else throw error;
    } finally {
      this.isLastLoading = false;
    }
  }

  private sendIdDocTypeIDForGA(): void {
    if (this.isFirstLoading || !this.$gtag) return;
    try {
      const sendDateTimeKey: string = moment.utc().tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss');

      this.$gtag.event('select_id_doc_type', {
        send_datetime: sendDateTimeKey,
        id_doc_type: this.idDocTypeId
      });
    } catch (_) {
    } finally {
      return;
    }
  }

  public get showIdDocSelect(): boolean {
    let idDocTypes: IdDocType[] = this.$store.getters['auth/idDocTypes'];

    // 個人番号書類のみ撮影の場合は非表示
    if (idDocTypes.length === 1 && idDocTypes[0] === IdDocType.MnGeneral) return false;

    return this.isIddocList;
  }
}
